import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  LABELING_STRATEGIES_REQUIRING_NAME,
  RegistryLabelingStrategy,
  TagSchema,
} from 'constants/RegistryConstants';
import {ClientId} from 'notebook/ELNConstants';
import {
  defaultStructuredTableColumnsForRegisterBioentity,
  filterOutNonDefaultColumns,
  getBioentityPrimarySamplesFromStructuredTable,
  getTagSchemaForStructuredTable,
} from 'notebook/integrations/registry/StructuredTableUtilsForRegistration';
import {ColumnDefinition, UNITS_COLUMN_NAME} from 'notebook/structured-tables/columns/ColumnDefinitions';
import isMissingFieldColumn from 'notebook/structured-tables/columns/isMissingFieldColumn';
import isReadOnly from 'notebook/structured-tables/columns/isReadOnly';
import {
  isMeasurementAmountStructuredTableColumn,
  isTagValueStructuredTableColumn,
  isUnitSelectorStructuredTableColumn,
} from 'notebook/structured-tables/columns/TypePredicates';
import GenericStructuredTableAdapter from 'notebook/structured-tables/GenericStructuredTableAdapter';
import MixtureStructuredTableRowUpdater from 'notebook/structured-tables/row-updaters/MixtureStructuredTableRowUpdater';
import {
  BaseStructuredTable,
  PrimarySample,
  SchemaAndColumnGroup,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableRow,
} from 'notebook/structured-tables/StructuredTableConstants';
import {fieldsPerForRowStructuredTableFormLayout} from 'notebook/structured-tables/StructuredTableForm';
import {getTypedValueFromCell, getTypedValueFromTable} from 'notebook/structured-tables/util/getTypedValue';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {StructuredTableInsertionContextValue} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {
  BaseTypedValueType,
  RowMetadata,
  Table,
  TableColumnType,
  TableDimension,
  TableReorderLocks,
  TypedValue,
} from 'notebook/tables/TableConstants';
import {
  coerceToPrimitive,
  getBioentityInfoFromTypedValue,
  getIdsFromTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {
  DEFAULT_MIXTURE_AMOUNT,
  DEFAULT_MIXTURE_UNITS,
  ORDERED_MIXTURE_UNITS_FOR_DISPLAY,
} from 'registry/mixtures/MixtureConstants';
import {SchemaFieldSelector} from 'schemas/SchemaFieldSelectorConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';

const AMOUNT_COLUMN_NAME = 'Target Amount';

export function _isParentLinkMixtureFieldColumn(
  column: StructuredTableColumn,
  parentLinkMixtureFieldId: string | null
): boolean {
  if (parentLinkMixtureFieldId == null) {
    return false;
  }
  return isTagValueStructuredTableColumn(column) && column.schemaFieldApiId === parentLinkMixtureFieldId;
}

function getMixtureAmountValue(table: MixtureMetadataStructuredTable): TypedValue | null {
  assert(table.rows.length === 1, 'Expected MixtureMetadataStructuredTable to have exactly 1 row');
  const rowIndex = 0;
  const columnIndex = table.columns.findIndex(
    (column) => column.columnType === TableColumnType.MEASUREMENT_AMOUNT
  );
  assert(
    columnIndex !== -1,
    'Expected MixtureMetadataStructuredTable columns to include a column for the mixture amount'
  );
  return getTypedValueFromTable(table, rowIndex, columnIndex);
}

export function getMixtureAmountFromTable(table: MixtureMetadataStructuredTable): string {
  const typedValue = getMixtureAmountValue(table);
  if (typedValue == null) {
    return DEFAULT_MIXTURE_AMOUNT.stringValue;
  }
  assert(
    typedValue.type === BaseTypedValueType.STRING,
    `Expected typed value for mixture amount cell to be of type STRING, but was ${typedValue.type}`
  );
  return coerceToPrimitive(typedValue) as string;
}

function getParentMixtureId(
  mixtureRow: StructuredTableRow<MixtureMetadataStructuredTableRowMetadata>,
  columns: Array<StructuredTableColumn>,
  tableMetadata: MixtureMetadataStructuredTableMetadata
): string | null {
  const columnIndex = columns.findIndex((column) =>
    _isParentLinkMixtureFieldColumn(column, tableMetadata.parentLinkMixtureFieldId)
  );
  if (columnIndex === -1) {
    return null;
  }
  const typedValue = getTypedValueFromCell(mixtureRow.cells[columnIndex]);
  if (typedValue == null) {
    return null;
  }
  const ids = getIdsFromTypedValue(typedValue);
  if (ids == null) {
    // The typedValue might be a placeholder introduced via template migration, or some other value which we
    // should ignore in this case.
    return null;
  }
  if (ids.length === 0) {
    return null;
  } else if (ids.length === 1) {
    return ids[0];
  } else {
    assert(false, 'Expected parent link mixture field to be single-select');
  }
}

export function getParentMixtureIdFromTable(table: MixtureMetadataStructuredTable): string | null {
  assert(table.rows.length === 1, 'Expected MixtureMetadataStructuredTable to have exactly 1 row');
  return getParentMixtureId(table.rows[0], table.columns, table.metadata);
}

function getColumnForParentMixtureField(
  columns: Array<StructuredTableColumn>,
  parentLinkMixtureFieldId: string
): StructuredTableColumn | undefined {
  return columns.find((column) => _isParentLinkMixtureFieldColumn(column, parentLinkMixtureFieldId));
}

function orderParentMixtureFieldColumnFirst(
  baseColumns: Array<StructuredTableColumn>,
  parentLinkMixtureFieldId: string | null
): Array<StructuredTableColumn> {
  if (parentLinkMixtureFieldId == null) {
    return baseColumns;
  }
  const columnForParentMixtureField = getColumnForParentMixtureField(baseColumns, parentLinkMixtureFieldId);
  assert(
    columnForParentMixtureField != null,
    'Expected column for parent mixture field to exist if parentMixtureFieldId is present'
  );
  const [schemaFieldColumns, otherColumns] = _.partition(baseColumns, (column) =>
    isTagValueStructuredTableColumn(column)
  );
  return [
    ...otherColumns,
    columnForParentMixtureField,
    ...schemaFieldColumns.filter((column) => column !== columnForParentMixtureField),
  ];
}

export interface MixtureMetadataStructuredTableMetadata {
  labelingStrategy: RegistryLabelingStrategy;
  parentLinkMixtureFieldId: string | null;
}
export interface MixtureMetadataStructuredTableRowMetadata extends RowMetadata {}

function mixtureMetadataStructuredTableFormLayout(
  columns: Array<ColumnDefinition>,
  formFields: Array<JSX.Element>
): JSX.Element | null {
  const topHalfFormControls: Array<JSX.Element> = [];
  const bottomHalfFormControls: Array<JSX.Element> = [];
  const topHalfColumnDefinitions: Array<ColumnDefinition> = [];
  const bottomHalfColumnDefinitions: Array<ColumnDefinition> = [];
  formFields.forEach((formField, columnIndex) => {
    const columnDefinition = columns[columnIndex];
    if (
      columnDefinition.type === TableColumnType.MEASUREMENT_AMOUNT ||
      columnDefinition.type === TableColumnType.UNIT_SELECTOR
    ) {
      bottomHalfFormControls.push(formField);
      bottomHalfColumnDefinitions.push(columnDefinition);
    } else {
      topHalfFormControls.push(formField);
      topHalfColumnDefinitions.push(columnDefinition);
    }
  });
  return (
    <Row bufferRowReset={true}>
      {fieldsPerForRowStructuredTableFormLayout(topHalfColumnDefinitions, topHalfFormControls)}
      <Col className="clearfix" />
      <hr
        className={classNames(
          css(styles.hr),
          BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE && 'notebook-export--mixture-metadata-hr'
        )}
      />
      {fieldsPerForRowStructuredTableFormLayout(bottomHalfColumnDefinitions, bottomHalfFormControls)}
    </Row>
  );
}

export default class MixtureMetadataStructuredTableAdapter extends GenericStructuredTableAdapter<
  MixtureMetadataStructuredTableMetadata,
  MixtureMetadataStructuredTableRowMetadata
> {
  getDisplayName(
    table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): string {
    return 'Create mixture preps';
  }

  override shouldShowAsForm(
    table: StructuredTable<MixtureMetadataStructuredTableMetadata, MixtureMetadataStructuredTableRowMetadata>
  ): boolean {
    assert(table.rows.length === 1, 'Mixture metadata table must have exactly one row!');
    return true;
  }

  override renderFormContents(
    columns: Array<ColumnDefinition>,
    formFields: Array<JSX.Element>
  ): JSX.Element | null {
    return mixtureMetadataStructuredTableFormLayout(columns, formFields);
  }

  override renderFormField(
    editor: JSX.Element,
    columnDefinition: ColumnDefinition,
    columnIndex: number
  ): JSX.Element {
    if (columnDefinition.type === TableColumnType.MEASUREMENT_AMOUNT) {
      return (
        <Col
          sm={2}
          className={classNames(
            css(!editor.props.viewer && styles.measurementAmount),
            !editor.props.viewer &&
              BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE &&
              'notebook-export--mixture-measurement-amount'
          )}
        >
          {editor}
        </Col>
      );
    }
    if (columnDefinition.type === TableColumnType.UNIT_SELECTOR) {
      return (
        <Col
          sm={2}
          className={classNames(
            css(!editor.props.viewer && styles.unitSelector),
            !editor.props.viewer &&
              BenchlingConfig.TEMP_UPDATED_MIXTURE_PREP_EXPORT_STYLE &&
              'notebook-export--mixture-unit-selector'
          )}
        >
          {editor}
        </Col>
      );
    }
    return super.renderFormField(editor, columnDefinition, columnIndex);
  }

  getBlankRowMetadata(
    tableMetadata: MixtureMetadataStructuredTableMetadata
  ): MixtureMetadataStructuredTableRowMetadata {
    return {};
  }

  override isPositionReadOnly(
    currentColumnIndex: number,
    row: StructuredTableRow<MixtureMetadataStructuredTableRowMetadata>,
    structuredTable: MixtureMetadataStructuredTable
  ): boolean {
    const {columns, schemas, metadata} = structuredTable;
    const currentColumn = columns[currentColumnIndex];
    if (isReadOnly(currentColumn) || isMissingFieldColumn(currentColumn, schemas)) {
      return true;
    }
    if (
      _isParentLinkMixtureFieldColumn(currentColumn, metadata.parentLinkMixtureFieldId) &&
      row.isFinalized
    ) {
      return true;
    }
    switch (currentColumn.columnType) {
      case TableColumnType.ALIAS:
      case TableColumnType.BIOENTITY_NAME:
        return row.isFinalized;
      case TableColumnType.UNIT_SELECTOR:
        // Lock Units column if a parent mixture value is set, since units are inherited from the parent
        return getParentMixtureId(row, columns, metadata) != null;
      default:
        return false;
    }
  }

  override injectFrontEndMetadata({
    column,
    schemas,
    tableMetadata,
  }: {
    column: StructuredTableColumn;
    schemas: Array<SchemaAndColumnGroup>;
    tableMetadata: MixtureMetadataStructuredTableMetadata;
  }): StructuredTableColumn {
    if (isUnitSelectorStructuredTableColumn(column)) {
      return {
        ...column,
        frontEndMetadata: {acceptedUnits: ORDERED_MIXTURE_UNITS_FOR_DISPLAY},
      };
    } else if (isMeasurementAmountStructuredTableColumn(column)) {
      return {...column, frontEndMetadata: {allowQS: false}};
    }
    return column;
  }

  override _getAllSupportedColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: MixtureMetadataStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    const baseColumns = orderParentMixtureFieldColumnFirst(
      super._getAllSupportedColumns({
        entryId,
        metadata,
        schemas,
        selectorsById,
        structuredTableInsertionContext,
      }),
      metadata.parentLinkMixtureFieldId
    );
    const amountColumn = makeStructuredTableColumn({
      columnType: TableColumnType.MEASUREMENT_AMOUNT,
      name: AMOUNT_COLUMN_NAME,
      isRequired: true,
      defaultValue: {
        typedValue: {
          type: BaseTypedValueType.STRING,
          rawValue: DEFAULT_MIXTURE_AMOUNT.stringValue,
        },
        text: '',
      },
    });
    const unitsColumn = makeStructuredTableColumn({
      columnType: TableColumnType.UNIT_SELECTOR,
      name: UNITS_COLUMN_NAME,
      isRequired: true,
      defaultValue: {
        typedValue: {type: BaseTypedValueType.STRING, rawValue: DEFAULT_MIXTURE_UNITS},
        text: '',
      },
    });
    return [
      // Note that while this includes an ALIAS column, it gets filtered out in getInitialColumns, and the
      // StructuredTableFormView provides no way to add or remove columns, so in practice there is currently
      // never an ALIAS column in this table
      ...defaultStructuredTableColumnsForRegisterBioentity({
        baseColumns,
        bioentityExists: false,
        entityNameColumnConfiguration: LABELING_STRATEGIES_REQUIRING_NAME.includes(metadata.labelingStrategy)
          ? 'REQUIRED'
          : 'ABSENT',
      }),
      amountColumn,
      unitsColumn,
    ];
  }

  override getInitialColumns({
    entryId,
    metadata,
    schemas,
    selectorsById,
    structuredTableInsertionContext,
  }: {
    entryId: string;
    metadata: MixtureMetadataStructuredTableMetadata;
    schemas: Array<SchemaAndColumnGroup>;
    selectorsById: {[p: string]: SchemaFieldSelector};
    structuredTableInsertionContext: StructuredTableInsertionContextValue;
  }): Array<StructuredTableColumn> {
    return filterOutNonDefaultColumns(
      super.getInitialColumns({
        entryId,
        metadata,
        schemas,
        selectorsById,
        structuredTableInsertionContext,
      })
    );
  }

  override toTable(
    structuredTable: StructuredTable<
      MixtureMetadataStructuredTableMetadata,
      MixtureMetadataStructuredTableRowMetadata
    >
  ): Table {
    return {...super.toTable(structuredTable), allowAddRows: false};
  }

  override getPrimarySamples(
    structuredTable: MixtureMetadataStructuredTable,
    computedTable?: Array<Array<EvaluatedCell>> | null
  ): {[rowId: string]: Array<PrimarySample>} {
    return getBioentityPrimarySamplesFromStructuredTable(structuredTable, computedTable);
  }

  getTagSchema(structuredTable: MixtureMetadataStructuredTable): TagSchema | null {
    return getTagSchemaForStructuredTable(structuredTable);
  }

  getMixtureId(table: MixtureMetadataStructuredTable): string | null {
    assert(table.rows.length === 1, 'Expected MixtureMetadataStructuredTable to have exactly 1 row');
    const bioentityColumnIndex = table.columns.findIndex(
      ({columnType}) => columnType === TableColumnType.EXISTING_BIOENTITY
    );
    const bioentityTypedValue = getTypedValueFromTable(table, 0, bioentityColumnIndex);
    return bioentityTypedValue ? (getBioentityInfoFromTypedValue(bioentityTypedValue)?.id ?? null) : null;
  }

  override getTableKindRowUpdater(): typeof MixtureStructuredTableRowUpdater {
    return MixtureStructuredTableRowUpdater;
  }

  override allowReorderDimensions(
    structuredTable: StructuredTable<
      MixtureMetadataStructuredTableMetadata,
      MixtureMetadataStructuredTableRowMetadata
    >
  ): TableReorderLocks {
    return {
      [TableDimension.ROW]: false,
      [TableDimension.COLUMN]: false,
    };
  }
}

const styles = StyleSheet.create({
  hr: {
    marginLeft: '10px',
    marginRight: '10px',
  },
  measurementAmount: {
    paddingRight: 0,
  },
  unitSelector: {
    paddingLeft: 0,
  },
});

export type GenericMixtureMetadataStructuredTable<TIds> = BaseStructuredTable<
  MixtureMetadataStructuredTableMetadata,
  MixtureMetadataStructuredTableRowMetadata,
  TIds
>;
export type MixtureMetadataStructuredTable = GenericMixtureMetadataStructuredTable<ClientId>;
